// ─────────────────────────────────────────────────────────────────────────────
// Design tokens — single source of truth for the OC Lab demo look & feel.
//
// Palette direction: Bode brand blue as structure, Bode lime as accent,
// Bode navy as ink, generous white space.
//
// Source: BODE Styleguide, corporate design guidelines, October 2024.
//
//   'The base'       #0B0D48  surfaces and text
//   'The access'     #4456F2  logo and graphic elements
//   'The innovation' #D2FF75  discreet accent
//
// The secondary ramps below are the guide's own gradations, which it states
// are 'mainly used for digital purposes, such as in presentations for
// diagrams and graphs or as discreet background areas'.
//
// Values marked DERIVED are not in the guide: they extend a ramp where the
// interface needs a step the guide does not give (a pressed state, a very
// light wash, or a body-text weight that meets WCAG AA on white).
// ─────────────────────────────────────────────────────────────────────────────

export const colors = {
  // ── Brand accent ──────────────────────────────────────────────────────────
  accent: '#D2FF75',        // 'the innovation' — accent, active state
  accentDeep: '#5F7F17',    // DERIVED — lime dark enough to read as text (4.6:1)
  accentPress: '#C4F45C',   // DERIVED — hover / pressed on a lime fill
  accentSoft: '#E4FFAC',    // secondary 1
  accentTint: '#EDFFC8',    // secondary 2
  accentWash: '#F6FFE3',    // secondary 3

  // Brand blue — carries structure: dark surfaces, headings, selected state,
  // data series. Yellow stays the accent; blue is the ground it sits on.
  blue: '#4456F2',          // 'the access' — logo and graphic elements
  blueDeep: '#2E3ECC',      // DERIVED — hover / pressed, deepest fills
  blueMid: '#8F9AF7',       // secondary 1
  blueTint: '#B4BBFA',      // secondary 2
  blueSoft: '#DADDFC',      // secondary 3
  blueWash: '#EFF1FE',      // DERIVED — very light background tint
  barTint: '#EAEDFD',       // DERIVED — top bar ground       // top bar ground — tune this alone to adjust the bar

  // ── Neutrals (anthracite family) ──────────────────────────────────────────
  ink: '#0B0D48',           // 'the base' — headings, primary text
  inkMuted: '#4A4C74',      // DERIVED — body text, 8:1 on white
  inkFaint: '#6D6E91',      // secondary 1 — captions, meta (5:1 on white)
  inkGhost: '#9D9EB6',      // secondary 2 — disabled, placeholder

  // ── Surfaces ──────────────────────────────────────────────────────────────
  bg: '#FFFFFF',            // page background
  bgAlt: '#F5F6FB',         // section / card-grid background
  surface: '#FFFFFF',       // card face
  surfaceRaised: '#FAFAFE', // hovered card face

  // ── Lines ─────────────────────────────────────────────────────────────────
  border: '#E2E8EF',        // DERIVED
  borderStrong: '#CECFDA',  // secondary 3

  // ── Dark section (topbar variant, booth screens, hero blocks) ─────────────
  dark: '#0B0D48',
  darkAlt: '#1B1F63',
  onDark: '#FFFFFF',
  onDarkMuted: 'rgba(255,255,255,0.66)',
  onDarkFaint: 'rgba(255,255,255,0.38)',
  onDarkBorder: 'rgba(255,255,255,0.14)',

  // ── Status ────────────────────────────────────────────────────────────────
  // Deliberately NOT lime — lime is the brand accent, so warnings use orange
  // and 'ok' stays a deep green, distinguishable from ordinary UI chrome.
  ok: '#2E7D53',
  okSoft: '#E6F2EC',
  warn: '#D07A00',
  warnSoft: '#FDF0DC',
  alert: '#C0322C',
  alertSoft: '#FBE9E8',
  info: '#4456F2',
  infoSoft: '#DADDFC',
} as const;

// Severity ramp for heat maps / anomaly scoring (1 = low … 5 = peak).
// Runs neutral → orange → red so it never collides with the brand yellow.
export const heat = [
  null,
  { fill: '#EEEFF8', stroke: '#CDD1E4', text: '#474D68', label: 'Low' },
  { fill: '#FDF3DC', stroke: '#E8C05C', text: '#8A6A12', label: 'Moderate' },
  { fill: '#FBE3C2', stroke: '#D89A3A', text: '#9A5D0B', label: 'High' },
  { fill: '#F6CDBC', stroke: '#CF6B45', text: '#9E3B1C', label: 'Very high' },
  { fill: '#E2554C', stroke: '#C0322C', text: '#FFFFFF', label: 'Peak' },
] as const;

// ── Typography ──────────────────────────────────────────────────────────────
// Work Sans is the BODE brand typeface (styleguide, October 2024). It is
// self-hosted via the @fontsource/work-sans package and imported in index.tsx,
// so it is bundled with the app and makes no network request at render time —
// trade-fair wifi cannot break it. System faces remain as the fallback.
//
// The guide's second face, Brigends Expanded, is a licensed display font used
// only for website headlines and presentation chapter titles; it is not used
// here. Calibri is reserved for e-mail and business letters.
export const font = {
  sans: '"Work Sans", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  mono: 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
} as const;

export const type = {
  eyebrow: {
    fontFamily: font.mono,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.18em',
    textTransform: 'uppercase' as const,
  },
  display: {
    fontFamily: font.sans,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.05,
  },
  heading: {
    fontFamily: font.sans,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
  },
  body: {
    fontFamily: font.sans,
    fontWeight: 400,
    lineHeight: 1.65,
  },
  meta: {
    fontFamily: font.mono,
    fontSize: '11px',
    letterSpacing: '0.06em',
  },
} as const;

// ── Layout ──────────────────────────────────────────────────────────────────
export const layout = {
  maxWidth: '1120px',
  /** Prototype pages: wider, to leave room for the fleet sidebar. */
  maxWidthWide: '1440px',
  topbarHeight: '64px',
  gutter: { xs: 3, md: 6 },
  radius: '4px',      // squared-off, industrial — panels, inputs, chips
  radiusLg: '6px',
  /**
   * Styleguide 'green bubble & button': the brand's calls to action are fully
   * rounded. CTAs and icon bubbles only — panels stay squared off so the
   * dashboard keeps its instrument feel.
   */
  radiusPill: '999px',
} as const;

export const elevation = {
  card: '0 1px 2px rgba(26,31,53,0.05)',
  cardHover: '0 6px 20px rgba(26,31,53,0.12)',
} as const;

// ── Brand gradients ─────────────────────────────────────────────────────────
// Styleguide, Design assets. The gradient element is always blue and always
// rotated 32°. In CSS a gradient line at 122deg puts its bands at 32° from the
// horizontal, which is why that angle appears throughout.

export const gradient = {
  /**
   * 'Color morph' — diagonal blue bands over the base navy, used as a striking
   * background. Hard colour stops keep the band edges crisp, as in the guide.
   * Band opacities were tuned against a render: any stronger and they compete
   * with the headline sitting on top of them.
   */
  colorMorph: [
    'linear-gradient(122deg, rgba(68,86,242,0) 0 30%, rgba(68,86,242,0.20) 30% 47%, rgba(68,86,242,0) 47%)',
    'linear-gradient(122deg, rgba(143,154,247,0) 0 58%, rgba(143,154,247,0.10) 58% 72%, rgba(143,154,247,0) 72%)',
    'linear-gradient(122deg, rgba(68,86,242,0) 0 80%, rgba(68,86,242,0.16) 80% 100%)',
    'linear-gradient(122deg, #0B0D48 0%, #12174F 55%, #0B0D48 100%)',
  ].join(', '),

  /** 'Gradient and round corners' — white through to the access blue, at 32°. */
  blueFade: 'linear-gradient(122deg, #FFFFFF 0%, #DADDFC 34%, #4456F2 100%)',
} as const;
